import { createFileRoute } from "@tanstack/react-router";
import { Check } from "@/components/animate-ui/icons/check";
import { PublicLayout } from "@/components/public-layout";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo";

export const Route = createFileRoute("/privacy-policy")({
	head: () => {
		const canonical = getCanonical("/privacy-policy");
		const meta = buildMetaTags({
			title: "Privacy Policy - m8tes",
			description:
				"Our privacy policy explains how m8tes collects, uses, and protects your personal information.",
			canonical,
			ogType: "website",
		});
		return {
			meta,
			links: buildLinks({ canonical }),
		};
	},
	component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
	return (
		<PublicLayout>
			<div className="container mx-auto px-4 py-12 max-w-4xl">
				<div className="prose prose-neutral dark:prose-invert max-w-none">
					<h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
					<p className="text-muted-foreground mb-8">Last Updated: February 18, 2026</p>

					{/* TLDR */}
					<section className="mb-12 p-6 bg-primary/10 border-l-4 border-primary rounded-r-lg">
						<h2 className="text-2xl font-semibold mb-4">TL;DR - What You Need to Know</h2>
						<ul className="space-y-3">
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>No AI Training on Your Data</strong> - We never use your content,
									conversations, or outputs to train AI models
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>You Own Your Data</strong> - Full ownership of all content and outputs you
									create
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>Delete Anytime</strong> - Request deletion of all your data at any time
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>GDPR &amp; CCPA Compliant</strong> - Full privacy rights for users
									worldwide
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>No Data Selling</strong> - We never sell or share your information with
									third parties for advertising
								</div>
							</li>
						</ul>
					</section>

					{/* Table of Contents */}
					<section className="mb-12">
						<h2 className="text-2xl font-semibold mb-4">Table of Contents</h2>
						<ol className="list-decimal pl-6 space-y-1">
							<li>Applicability of This Privacy Policy</li>
							<li>Information We Collect</li>
							<li>Google User Data</li>
							<li>How AI Processes Your Data</li>
							<li>How We Use Your Information</li>
							<li>Cookies and Tracking Technologies</li>
							<li>How We Share Your Information</li>
							<li>Data Security</li>
							<li>Data Retention</li>
							<li>International Data Transfers</li>
							<li>Your Privacy Rights</li>
							<li>Children's Privacy</li>
							<li>Third-Party Links</li>
							<li>Changes to This Privacy Policy</li>
							<li>Contact Us</li>
						</ol>
					</section>

					{/* 1. Applicability */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							1. Applicability of This Privacy Policy
						</h2>
						<p>
							m8tes, Inc. ("<strong>m8tes</strong>," "<strong>we</strong>," "<strong>us</strong>,"
							or "<strong>our</strong>") is committed to protecting your privacy. This Privacy
							Policy explains how we collect, use, disclose, and safeguard your information when you
							visit our website at m8tes.ai (our "<strong>Website</strong>"), use our autonomous AI
							agent platform (the "<strong>Service</strong>"), or otherwise interact with us.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">Controller and Processor Roles</h3>
						<p>m8tes operates in two capacities depending on the type of data:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Data Controller:</strong> For personal data we collect directly from you
								through your account, our Website, and your interactions with us (described in this
								Privacy Policy). This includes Account Information, Usage Data, and Communication
								Information.
							</li>
							<li>
								<strong>Data Processor:</strong> When our API customers submit data through the
								m8tes platform on behalf of their end users (including task instructions, run
								inputs/outputs, and end-user identifiers). We process this data ("
								<strong>Customer Data</strong>") on behalf of and under the instructions of our
								customers. Customer Data is governed by our Customer Agreement and Data Processing
								Agreement (DPA), not this Privacy Policy. Queries about Customer Data should be
								directed to the relevant m8tes customer who is the data controller.
							</li>
						</ul>
						<p className="mt-4">
							This Privacy Policy governs the processing of personal data for which m8tes is the
							data controller. For API customers, a Data Processing Agreement is available upon
							request at privacy@m8tes.ai.
						</p>
						<p className="mt-4">
							Please read this Privacy Policy carefully. By accessing or using our Service, you
							acknowledge that you have read, understood, and agree to be bound by this Privacy
							Policy. If you do not agree with our policies and practices, please do not use our
							Service.
						</p>
					</section>

					{/* 2. Information We Collect */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">2. Information We Collect</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">2.1 Information You Provide</h3>
						<p>We collect information that you provide directly to us:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Account Information:</strong> Email address, name, phone number (optional),
								and password when you create an account
							</li>
							<li>
								<strong>Agent Configuration:</strong> Agent names, roles, instructions, goals, and
								preferences you set for your autonomous teammates
							</li>
							<li>
								<strong>Task and Conversation Data:</strong> Instructions, queries, messages, and
								content you submit to our Service, including AI-generated responses
							</li>
							<li>
								<strong>Integration Credentials:</strong> When you connect third-party services, we
								collect and securely store authentication credentials (OAuth tokens, API keys)
							</li>
							<li>
								<strong>Billing Information:</strong> Payment information is processed by Stripe,
								our third-party payment processor. We store only transaction references,
								subscription status, and billing contact details
							</li>
							<li>
								<strong>Communication Information:</strong> When you contact us for support, provide
								feedback, or otherwise communicate with us, we collect the contents of those
								communications
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							2.2 Information We Collect Automatically
						</h3>
						<p>When you use our Service, we automatically collect certain information:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Usage Data:</strong> Request timestamps, response times, token counts,
								execution metrics, task completion status, features used, and how you interact with
								the Service
							</li>
							<li>
								<strong>Log Data:</strong> IP addresses, request methods, URL paths, HTTP status
								codes, and error messages for security and diagnostic purposes
							</li>
							<li>
								<strong>Device Information:</strong> Browser type, operating system, device
								identifiers, and screen resolution
							</li>
							<li>
								<strong>Cost and Billing Metrics:</strong> API usage costs and execution time for
								billing and analytics
							</li>
							<li>
								<strong>Cookies and Similar Technologies:</strong> See Section 6 for details on how
								we use cookies and analytics tools
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							2.3 Information from API and SDK Usage
						</h3>
						<p>If you use our developer API or SDK, we additionally collect:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>API Key Metadata:</strong> Key identifiers, creation dates, and usage
								patterns (not the key values themselves)
							</li>
							<li>
								<strong>Webhook Configuration:</strong> Endpoint URLs, delivery status, and response
								codes for webhook integrations you configure
							</li>
							<li>
								<strong>SDK Telemetry:</strong> SDK version, request volume, and error rates
							</li>
						</ul>
					</section>

					{/* 3. Google User Data */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							3. Google User Data (When You Connect Google Integrations)
						</h2>
						<p className="mb-4">
							<strong>This section only applies if you choose to connect Google services</strong>{" "}
							(such as Google Ads or Google OAuth) to your m8tes account. If you do not connect
							these integrations, we do not collect any Google user data.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							3.1 What Google User Data We Collect
						</h3>
						<p>When you actively connect Google integrations to m8tes, we collect:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Google Account Information:</strong> Your email address, name, and profile
								information provided during OAuth authentication
							</li>
							<li>
								<strong>Google Ads Customer IDs:</strong> The customer account identifiers you
								authorize us to access
							</li>
							<li>
								<strong>Authentication Credentials:</strong> OAuth tokens (access tokens and refresh
								tokens) required to authenticate your requests to Google's services
							</li>
						</ul>
						<p className="mt-4">
							<strong>Important:</strong> We do <em>not</em> permanently store your Google Ads
							campaign data, metrics, or advertising information. We provide you with a query tool
							(GAQL - Google Ads Query Language) that allows you to retrieve data directly from
							Google on demand. Query results are temporarily processed to display to you but are
							not saved to our databases unless you explicitly save specific information within the
							Service.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">3.2 How We Use Google User Data</h3>
						<p>We use Google user data solely to provide you with the services you requested:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Authentication:</strong> To verify your identity and maintain your login
								session via Google OAuth
							</li>
							<li>
								<strong>Execute Queries:</strong> To run Google Ads Query Language (GAQL) queries on
								your behalf and retrieve advertising data you request
							</li>
							<li>
								<strong>Display Results:</strong> To process and display query results, campaign
								performance data, and metrics within our interface
							</li>
							<li>
								<strong>Maintain Access:</strong> To refresh authentication tokens and maintain
								authorized access to your connected Google Ads accounts
							</li>
						</ul>
						<p className="mt-4">
							<strong>We do NOT use Google user data for:</strong>
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Training artificial intelligence or machine learning models</li>
							<li>Targeted advertising, personalized advertising, or retargeted advertising</li>
							<li>Selling to data brokers or information resellers</li>
							<li>Determining credit-worthiness or lending purposes</li>
							<li>Any purpose unrelated to providing or improving m8tes functionality</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							3.3 How We Share, Transfer, or Disclose Google User Data
						</h3>
						<p>
							We do <strong>not</strong> sell, rent, or transfer your Google user data to third
							parties. We only share Google user data in the following limited circumstances:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>With Google Services:</strong> We transmit authentication credentials and
								query requests to Google's APIs solely to execute the services you requested (e.g.,
								running GAQL queries against your Google Ads accounts)
							</li>
							<li>
								<strong>With Your Consent:</strong> If you explicitly authorize us to share data
								with additional third-party services you connect
							</li>
							<li>
								<strong>Legal Compliance:</strong> If required by law, court order, or government
								regulation (as described in Section 7.3)
							</li>
						</ul>
						<p className="mt-4">
							We do <strong>not</strong> transfer or disclose Google user data to third parties for
							purposes other than providing you with the m8tes Service functionality you requested.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">3.4 Security of Google User Data</h3>
						<p>We protect your Google user data with industry-standard security measures:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Encryption:</strong> All Google OAuth tokens (access tokens, refresh tokens)
								are encrypted at rest using AES-256 encryption and transmitted over secure HTTPS
								connections
							</li>
							<li>
								<strong>Access Controls:</strong> Access to Google user data is restricted to
								authorized systems and personnel only
							</li>
							<li>
								<strong>Secure Storage:</strong> Authentication credentials are stored in encrypted
								database fields with strict access policies
							</li>
							<li>
								<strong>Token Management:</strong> We use short-lived access tokens and securely
								manage refresh token rotation
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							3.5 Retention and Deletion of Google User Data
						</h3>
						<p>We retain Google user data only as long as necessary:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Authentication Credentials:</strong> Stored for as long as you maintain the
								Google integration connection. You may disconnect at any time through your account
								settings.
							</li>
							<li>
								<strong>Query Results:</strong> Temporarily processed for display but not
								permanently stored unless you explicitly save specific data
							</li>
							<li>
								<strong>Account Deletion:</strong> When you delete your m8tes account or disconnect
								the Google integration, we immediately delete all associated Google authentication
								credentials and any stored Google user data
							</li>
						</ul>
						<p className="mt-4">
							<strong>You may request deletion of your Google user data at any time</strong> by
							emailing privacy@m8tes.ai or by disconnecting the Google integration in your account
							settings.
						</p>
					</section>

					{/* 4. How AI Processes Your Data */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">4. How AI Processes Your Data</h2>
						<p>
							m8tes is an AI-powered platform. Understanding how your data interacts with AI systems
							is important to us.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.1 AI Service Provider</h3>
						<p>
							We use <strong>Anthropic's Claude API</strong> as our primary AI service provider.
							When you submit tasks, messages, or instructions to the Service, this content is sent
							to Anthropic's API for processing. Anthropic processes this data to generate responses
							and execute your agent's tasks.
						</p>
						<p className="mt-4">
							Under Anthropic's commercial API terms, they do <strong>not</strong> use your inputs
							or outputs to train their AI models. Anthropic may retain API inputs and outputs for a
							limited period (typically 30 days) for trust and safety purposes, after which the data
							is deleted. For full details, refer to Anthropic's privacy policy and commercial
							terms.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.2 What Data Is Sent to AI</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>Task instructions and conversation messages you submit</li>
							<li>Agent configuration (system prompts, instructions, goals)</li>
							<li>Context from connected tools and integrations needed to complete your tasks</li>
							<li>Conversation history within a run for continuity</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.3 Our Commitments</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>No Model Training:</strong> Neither m8tes nor our AI providers use your
								content, conversations, or outputs to train AI models
							</li>
							<li>
								<strong>No Automated Decision-Making:</strong> We do not use AI to make automated
								decisions that produce legal effects or similarly significant effects concerning you
								without human involvement
							</li>
							<li>
								<strong>Sandbox Isolation:</strong> Agent executions run in isolated sandbox
								environments. Code and files generated during execution are confined to your account
								and are not accessible to other users
							</li>
						</ul>
					</section>

					{/* 5. How We Use Your Information */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">5. How We Use Your Information</h2>
						<p>We use the information we collect to:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Provide and Improve the Service:</strong> Execute your tasks, run autonomous
								agents, and continuously improve our platform
							</li>
							<li>
								<strong>Process Transactions:</strong> Manage your subscription, process payments,
								and send billing-related communications
							</li>
							<li>
								<strong>Maintain Integrations:</strong> Connect to and authenticate with third-party
								services you authorize
							</li>
							<li>
								<strong>Security and Fraud Prevention:</strong> Detect, prevent, and address
								security incidents and fraudulent activity
							</li>
							<li>
								<strong>Customer Support:</strong> Respond to your questions, troubleshoot issues,
								and provide technical assistance
							</li>
							<li>
								<strong>Analytics and Development:</strong> Analyze usage patterns to improve
								features, develop new capabilities, and optimize performance
							</li>
							<li>
								<strong>Communications:</strong> Send you service-related notifications, updates,
								and (where permitted) marketing communications
							</li>
							<li>
								<strong>Compliance:</strong> Comply with legal obligations and enforce our terms of
								service
							</li>
						</ul>

						<p className="mt-4">
							We may aggregate or de-identify personal information so that you can no longer be
							identified, and use such data for research, analytics, and improving our Service. We
							do not attempt to re-identify this information.
						</p>
					</section>

					{/* 6. Cookies and Tracking Technologies */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							6. Cookies and Tracking Technologies
						</h2>
						<p>
							We use cookies and similar technologies to operate our Service, analyze usage, and
							improve your experience.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.1 What We Use</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Essential Cookies:</strong> Required for authentication, session management,
								and security. These cannot be disabled without affecting Service functionality.
							</li>
							<li>
								<strong>Analytics (PostHog):</strong> We use PostHog, a product analytics platform,
								to understand how users interact with our Service. PostHog collects usage data such
								as pages visited, features used, and interaction patterns. This data helps us
								improve the Service. PostHog data is not shared with third parties for advertising.
							</li>
							<li>
								<strong>Error Tracking (Sentry):</strong> We use Sentry to monitor application
								errors and performance. Sentry may collect technical data including browser type,
								error stack traces, and request metadata to help us diagnose and fix issues.
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.2 Your Choices</h3>
						<p>
							Most web browsers allow you to control cookies through their settings. You can set
							your browser to refuse all or some cookies, or to alert you when cookies are being
							sent. If you disable cookies, some parts of the Service may not function properly.
						</p>
						<p className="mt-4">
							For EEA/UK users, we obtain consent before placing non-essential cookies on your
							device, in accordance with applicable law.
						</p>
					</section>

					{/* 7. How We Share Your Information */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">7. How We Share Your Information</h2>
						<p>
							We do not sell your personal information. We may share your information with the
							following categories of recipients:
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							7.1 Sub-Processors and Service Providers
						</h3>
						<p>
							We engage the following categories of third-party service providers to help us operate
							and deliver the Service:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Anthropic</strong> (AI processing) — Powers our autonomous agents. Processes
								task instructions, messages, and conversation data to generate responses.
							</li>
							<li>
								<strong>Stripe</strong> (payment processing) — Processes subscription payments and
								manages billing. Receives payment method details directly from you.
							</li>
							<li>
								<strong>PostHog</strong> (product analytics) — Collects usage data to help us
								understand and improve the Service.
							</li>
							<li>
								<strong>Sentry</strong> (error monitoring) — Monitors application health and
								collects error data for diagnostics.
							</li>
							<li>
								<strong>Cloud Infrastructure Provider</strong> (hosting) — Hosts our application,
								databases, and computing resources.
							</li>
							<li>
								<strong>Email Service Provider</strong> (communications) — Sends transactional
								emails such as verification, notifications, and billing receipts.
							</li>
							<li>
								<strong>Composio</strong> (third-party integrations) — Facilitates connections to
								third-party tools and services you authorize your agents to use.
							</li>
						</ul>
						<p className="mt-4">
							All sub-processors are bound by contractual obligations to protect your data and may
							only process it for the purposes specified in our agreements with them.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">7.2 Third-Party Integrations</h3>
						<p>
							When you connect third-party services to your agents, we share relevant data with
							those services as necessary to execute your tasks. These integrations are initiated by
							you and are subject to the privacy policies of those third-party services.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">7.3 Legal Requirements</h3>
						<p>
							We may disclose your information if required by law, court order, or government
							regulation, or if we believe such action is necessary to:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Comply with legal obligations</li>
							<li>Protect our rights, property, or safety</li>
							<li>Prevent fraud or security threats</li>
							<li>Protect the rights and safety of our users</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">7.4 Business Transfers</h3>
						<p>
							In the event of a merger, acquisition, reorganization, or sale of assets, your
							information may be transferred as part of that transaction. We will notify you of any
							such change and any choices you may have regarding your personal information.
						</p>
					</section>

					{/* 8. Data Security */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">8. Data Security</h2>
						<p>
							We implement appropriate technical and organizational measures to protect your
							information:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Encryption:</strong> We encrypt sensitive data including authentication
								tokens and third-party credentials both in transit (TLS) and at rest (AES-256)
							</li>
							<li>
								<strong>Secure Authentication:</strong> Passwords are hashed using industry-standard
								algorithms and we support OAuth 2.0 for third-party authentication
							</li>
							<li>
								<strong>Access Controls:</strong> We restrict access to personal information to
								authorized personnel only, using role-based access controls
							</li>
							<li>
								<strong>Monitoring:</strong> We monitor our systems for security vulnerabilities and
								unauthorized access
							</li>
							<li>
								<strong>Sandbox Isolation:</strong> Agent code executions run in isolated sandbox
								environments to prevent cross-user data access
							</li>
						</ul>
						<p className="mt-4">
							However, no method of transmission over the internet or electronic storage is 100%
							secure. While we strive to use commercially acceptable means to protect your
							information, we cannot guarantee absolute security.
						</p>
					</section>

					{/* 9. Data Retention */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">9. Data Retention</h2>
						<p>
							We retain your information for as long as necessary to fulfill the purposes described
							in this Privacy Policy. Specific retention periods include:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Account Information:</strong> Retained for the life of your account and
								deleted within 30 days of account deletion, unless required by law
							</li>
							<li>
								<strong>Task and Conversation Data:</strong> Retained for the life of your account.
								Deleted when you delete your account or upon request
							</li>
							<li>
								<strong>Run Execution Logs:</strong> Retained for up to 90 days for debugging and
								support, then automatically purged
							</li>
							<li>
								<strong>Billing and Transaction Records:</strong> Retained for 7 years as required
								by tax and accounting regulations
							</li>
							<li>
								<strong>Server Logs:</strong> Retained for up to 90 days for security and diagnostic
								purposes
							</li>
							<li>
								<strong>Analytics Data:</strong> Retained in aggregated or de-identified form
								indefinitely for product improvement
							</li>
						</ul>
						<p className="mt-4">
							When you delete your account, we will delete or anonymize your personal information
							within 30 days, except where we are required to retain it by law. Conversation logs,
							task execution history, and agent configurations associated with your account will be
							deleted along with your account.
						</p>
					</section>

					{/* 10. International Data Transfers */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">10. International Data Transfers</h2>
						<p>
							m8tes is headquartered in the United States. Your information may be transferred to
							and processed in the United States and other countries where our service providers
							operate. These countries may have different data protection laws than your
							jurisdiction.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.1 Transfer Mechanisms</h3>
						<p>
							When we transfer personal data from the European Economic Area ("EEA"), United Kingdom
							("UK"), or Switzerland to countries that have not been deemed to provide an adequate
							level of data protection, we rely on appropriate safeguards including:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Standard Contractual Clauses (SCCs):</strong> We use the European
								Commission-approved Standard Contractual Clauses to ensure appropriate safeguards
								for data transfers
							</li>
							<li>
								<strong>Adequacy Decisions:</strong> Where applicable, we transfer data to countries
								recognized as providing adequate data protection
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">10.2 Onward Transfers</h3>
						<p>
							We ensure that our sub-processors who receive personal data from the EEA, UK, or
							Switzerland are also bound by appropriate data transfer mechanisms. We remain
							accountable for the processing of personal data by our sub-processors.
						</p>
					</section>

					{/* 11. Your Privacy Rights */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">11. Your Privacy Rights</h2>
						<p>
							Depending on your location, you may have the following rights regarding your personal
							information:
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">11.1 General Rights</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Access:</strong> Request a copy of the personal information we hold about
								you
							</li>
							<li>
								<strong>Correction:</strong> Request correction of inaccurate or incomplete
								information
							</li>
							<li>
								<strong>Deletion:</strong> Request deletion of your personal information, subject to
								certain exceptions
							</li>
							<li>
								<strong>Data Portability:</strong> Request a copy of your data in a structured,
								machine-readable format
							</li>
							<li>
								<strong>Opt-Out:</strong> Opt out of marketing communications at any time
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							11.2 Rights for EEA/UK Users (GDPR)
						</h3>
						<p>
							If you are located in the European Economic Area or United Kingdom, you have
							additional rights under the General Data Protection Regulation (GDPR):
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Right to Object:</strong> Object to processing of your personal information
								for direct marketing or legitimate interests
							</li>
							<li>
								<strong>Right to Restrict Processing:</strong> Request that we limit how we use your
								information
							</li>
							<li>
								<strong>Right to Withdraw Consent:</strong> Where we rely on consent, you may
								withdraw it at any time without affecting the lawfulness of prior processing
							</li>
							<li>
								<strong>Right to Lodge a Complaint:</strong> File a complaint with your local data
								protection supervisory authority
							</li>
						</ul>

						<h4 className="text-xl font-semibold mt-6 mb-3">Legal Basis for Processing (GDPR)</h4>
						<p>We process your personal information on the following legal bases:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Contractual Necessity:</strong> To create and maintain your account, provide
								the Service, process payments, and deliver customer support
							</li>
							<li>
								<strong>Legitimate Interests:</strong> To improve and develop our Service, analyze
								usage patterns, ensure security, and prevent fraud. We balance our interests against
								your privacy rights
							</li>
							<li>
								<strong>Legal Obligation:</strong> To comply with applicable laws, regulations, or
								legal processes
							</li>
							<li>
								<strong>Consent:</strong> For marketing communications, non-essential cookies, and
								any other processing where consent is required. You may withdraw consent at any time
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							11.3 Rights for US Residents (CCPA/State Privacy Laws)
						</h3>
						<p>
							If you are a resident of California or another US state with applicable privacy
							legislation, you may have additional rights:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Right to Know:</strong> Request information about the categories and
								specific pieces of personal information we have collected, the sources of
								collection, the purposes for collection, and the categories of third parties with
								whom we share it
							</li>
							<li>
								<strong>Right to Delete:</strong> Request deletion of your personal information,
								subject to certain exceptions
							</li>
							<li>
								<strong>Right to Correct:</strong> Request correction of inaccurate personal
								information
							</li>
							<li>
								<strong>Right to Opt-Out of Sale/Sharing:</strong> m8tes does{" "}
								<strong>not sell</strong> your personal information and does{" "}
								<strong>not share</strong> your personal information with third parties for
								cross-context behavioral advertising
							</li>
							<li>
								<strong>Right to Non-Discrimination:</strong> We will not discriminate against you
								for exercising any of your privacy rights
							</li>
						</ul>
						<p className="mt-4">
							<strong>Categories of personal information we collect</strong> (as defined by the
							CCPA): identifiers (name, email, IP address), commercial information (transaction
							history, subscription status), internet activity (usage data, log data), and
							professional information (job title, company name where provided).
						</p>
						<p className="mt-4">
							We do not sell personal information, and to our knowledge, we do not sell personal
							information of minors under 18 years of age.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">11.4 How to Exercise Your Rights</h3>
						<p>
							To exercise any of these rights, please contact us at privacy@m8tes.ai. We will
							respond to your request within 30 days (or as required by applicable law). We may need
							to verify your identity before processing your request.
						</p>
					</section>

					{/* 12. Children's Privacy */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">12. Children's Privacy</h2>
						<p>
							Our Service is not intended for individuals under the age of 18. We do not knowingly
							collect personal information from children. If you believe we have collected
							information from a child, please contact us immediately at privacy@m8tes.ai and we
							will delete such information.
						</p>
					</section>

					{/* 13. Third-Party Links */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">13. Third-Party Links</h2>
						<p>
							Our Service may contain links to third-party websites, services, or applications that
							are not operated by us. This Privacy Policy applies only to our Service. We have no
							control over, and assume no responsibility for, the content, privacy policies, or
							practices of any third-party sites or services. We encourage you to review the privacy
							policies of any third-party services you access through our platform.
						</p>
					</section>

					{/* 14. Changes to This Privacy Policy */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							14. Changes to This Privacy Policy
						</h2>
						<p>
							We may update this Privacy Policy from time to time to reflect changes in our
							practices or legal requirements. We will provide at least 30 days' advance notice of
							any material changes by:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Posting the updated policy on this page</li>
							<li>Updating the "Last Updated" date at the top</li>
							<li>Sending you an email notification for significant changes</li>
						</ul>
						<p className="mt-4">
							Your continued use of the Service after the changes take effect indicates your
							acceptance of the updated Privacy Policy.
						</p>
					</section>

					{/* 15. Contact Us */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">15. Contact Us</h2>
						<p>
							If you have questions, concerns, or requests regarding this Privacy Policy or our data
							practices, please contact us:
						</p>
						<div className="bg-muted p-6 rounded-lg mt-4 space-y-2">
							<p>
								<strong>m8tes, Inc.</strong>
							</p>
							<p>Privacy inquiries: privacy@m8tes.ai</p>
							<p>General support: support@m8tes.ai</p>
						</div>
						<p className="mt-4">
							<strong>For EEA/UK Users:</strong> m8tes, Inc. is the data controller of your personal
							information as described in this Privacy Policy. For inquiries about Customer Data
							processed on behalf of our API customers, please contact the relevant m8tes customer
							directly.
						</p>
					</section>

					{/* Acknowledgment */}
					<section className="mb-8 p-6 bg-muted rounded-lg">
						<p className="text-sm">
							By using our Service, you acknowledge that you have read and understood this Privacy
							Policy and agree to our collection, use, and disclosure of your information as
							described herein.
						</p>
					</section>
				</div>
			</div>
		</PublicLayout>
	);
}
